import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, Suspense, useEffect, memo, useCallback } from "react";
import { Grid, Button, Dialog, DialogTitle, DialogActions, LinearProgress, List, ListItem, Typography, CircularProgress } from "@material-ui/core";
import { useFetcher } from "@rest-hooks/core";
import { makeStyles } from "@material-ui/core/styles";
import Link from "@material-ui/core/Link";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import ClearIcon from "@material-ui/icons/Clear";
import { useTenantImage, useThemeComponent } from "@ploy-ui/tenants";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { SigningDocumentResource, useResourceWithInvalidate, ApplicationSignerResource, UploadedDocumentResource, VendorDocumentGroupResource, DocumentSettingsResource } from "@ploy-lib/rest-resources";
import { Dropzone } from "@ploy-ui/form-fields";
import { useAppLoad } from "../../appLoadContext";
import { useResource } from "@rest-hooks/core";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { useSearchParams } from "react-router-dom";
import DescriptionIcon from "@material-ui/icons/Description";
import { ImageFit } from "@ploy-ui/core";
import clsx from "clsx";
import { LabelComponent } from "../../field";
const useStyles = makeStyles(theme => ({
    dropZoneArea: {
        borderWidth: "1px",
        borderStyle: "dashed",
        margin: theme.spacing(0.5),
        color: "black",
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.primary.main
    },
    dropZoneInner: {
        gap: theme.spacing(2),
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing(2, 0),
        width: "inherit"
    },
    circleIconContainer: { marginLeft: theme.spacing(2), padding: "unset" },
    clearIcon: {
        float: "right"
    },
    dropZoneTextContainer: { padding: theme.spacing(0), margin: "unset" },
    dropZoneTextUpper: { fontSize: "16px", fontWeight: 771, color: "#00002B" },
    dropZoneTextLower: { fontSize: "14px", fontWeight: 771, color: "#404060" },
    documentListSection: {},
    documentListCard: {
        border: "1px",
        borderStyle: "solid",
        alignItems: "center",
        borderRadius: "16px",
        backgroundColor: "white",
        padding: theme.spacing(2, 2),
        margin: theme.spacing(1, 0)
    },
    documentListHeader: { margin: theme.spacing(1, 0), fontWeight: 775 },
    sectionTitle: { margin: theme.spacing(0.5) },
    sectionTitleText: {},
    fallbackAnimation: {
        animation: `$fading 1.2s ease-in-out infinite`
    },
    documentName: {
        fontSize: 18,
        color: "#00002B"
    },
    documentFilesize: {
        fontSize: 14,
        color: "#404060"
    },
    "@keyframes fading": {
        "0%": { opacity: 0.2 },
        "50%": { opacity: 1 },
        "100%": { opacity: 0.2 }
    }
}), { name: "DployDocumentDropzoneSection" });
const intlMessages = defineMessages({
    uploadTitle: {
        id: "template-form.sections.document-dropzone.title",
        description: "Upload contract",
        defaultMessage: "Upload contract"
    },
    uploadHelpertext: {
        id: "template-form.sections.document-dropzone.helpertext",
        description: "Upload the signed contract if you have it.",
        defaultMessage: "If you have a signed copy of the contract you can upload it here. If you do not have a signed copy of the contract it can be sent later."
    }
});
const uploadDocument = (file, documentType, customerContext) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    if (documentType)
        formData.append("documentType", documentType);
    if (customerContext)
        formData.append("customerContext", customerContext);
    return fetch(legacyApiResourceUrl("DocumentGrid/Upload"), {
        method: "POST",
        body: formData
    });
};
// @todo: This is a legacy API call, and should be replaced with a REST call using the rest-hooks library.
const deleteDocument = documentId => {
    return fetch(legacyApiResourceUrl("DocumentGrid/DeleteUploadedDocument"), {
        method: "POST",
        headers: new Headers({
            "Content-type": "application/json"
        }),
        body: JSON.stringify({
            documentId: documentId
        })
    });
};
const ConfirmDialog = ({ open, text, onConfirm, onDeny }) => {
    return (_jsxs(Dialog, { open: open, children: [_jsx(DialogTitle, { children: text }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onDeny, color: "primary", children: _jsx(FormattedMessage, { id: "template-form.sections.document-upload.confirm-no", defaultMessage: "No" }) }), _jsx(Button, { onClick: onConfirm, color: "primary", children: _jsx(FormattedMessage, { id: "template-form.sections.document-upload.confirm-yes", defaultMessage: "Yes" }) })] })] }));
};
addRegisteredSectionLayout({
    name: "DocumentDropzoneSection",
    displayName: "DocumentDropzoneSection",
    settings: {
        editableOptions: {
            allowEditSameSession: true
        }
    }
});
const DocumentDropzoneSection = (props) => {
    const classes = useStyles(props);
    const intl = useIntl();
    const { className, onClick } = props;
    const [showHelperText, setShowHelperText] = useState(false);
    const toggleHelperText = useCallback((e) => {
        setShowHelperText(show => !show);
        e.stopPropagation();
    }, []);
    return (_jsxs(Grid, { container: true, className: className, onClick: onClick, children: [_jsx(Grid, { item: true, xs: 12, className: classes.sectionTitle, children: _jsx(LabelComponent, { labelText: _jsx(Typography, { variant: "caption", className: classes.sectionTitleText, children: intl.formatMessage(intlMessages.uploadTitle) }), trimmedHelperText: intl.formatMessage(intlMessages.uploadHelpertext), showHelperText: showHelperText, toggleHelperText: toggleHelperText }) }), _jsx(Suspense, { fallback: _jsx(DropzoneLoadingFallback, {}), children: _jsx(DropzoneComponent, {}) })] }));
};
DocumentDropzoneSection.displayName = "DployDocumentDropzoneSection";
export { DocumentDropzoneSection };
const DropzoneLoadingFallback = () => {
    const classes = useStyles();
    return (_jsx(Grid, { container: true, justifyContent: "space-between", alignItems: "center", id: "DropzoneSuspenseFallback", style: { height: 80, borderRadius: 4 }, className: clsx(classes.fallbackAnimation, classes.dropZoneArea), children: _jsx(CircularProgress, { style: { margin: "0 auto" } }) }));
};
const DropzoneComponent = memo(() => {
    const docIcon = useTenantImage("Factoring");
    const intl = useIntl();
    const { applicationNumber, id: appSessionId } = useAppLoad();
    const isDirectUser = true;
    const [searchParams, _] = useSearchParams();
    const customerContext = searchParams.get("customerContext");
    const refetchSigningDocuments = useFetcher(SigningDocumentResource.list());
    const refetchSigners = useFetcher(ApplicationSignerResource.list());
    const documentList = useResourceWithInvalidate(UploadedDocumentResource.list(), appSessionId ? { applicationNumber, customerContext } : null);
    const [initialDocuments, setInitialDocuments] = useState();
    useEffect(() => {
        if (initialDocuments)
            return;
        setInitialDocuments(documentList?.map(x => x.documentId) ?? []);
    }, [documentList, initialDocuments]);
    const refetchDocuments = useFetcher(UploadedDocumentResource.list());
    // only deletes from the cache, we don't have a method that overrides the delete method in rest-hooks
    const deleteDocumentFromResource = useFetcher(UploadedDocumentResource.delete());
    const fetchDocGroups = !isDirectUser && !!appSessionId;
    const documentGroups = useResource(VendorDocumentGroupResource.list(), fetchDocGroups ? { applicationNumber } : null);
    // Fetch hooks end
    const [selectedGroup, setSelectedGroup] = useState(null);
    useEffect(() => {
        let defaultGroup = documentGroups?.find(dg => dg.isDefaultGroup);
        if (defaultGroup)
            setSelectedGroup(defaultGroup);
    }, [documentGroups]);
    const classes = useStyles();
    const [errorMessages, setErrorMessages] = useState([]);
    const ThemeAddDocumentUploadIcon = useThemeComponent("AddDocumentUpload");
    const AddDocumentUploadIcon = ThemeAddDocumentUploadIcon ?? AddCircleIcon;
    const filteredDocumentGroups = documentGroups &&
        documentGroups.filter(dg => dg.allowMultipleUploads ||
            !(documentList &&
                documentList.some(d => d.documentType === dg.documentCode)));
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    useEffect(() => {
        if (filteredDocumentGroups && filteredDocumentGroups.length === 1)
            setSelectedGroup(filteredDocumentGroups[0]);
        else if (selectedGroup &&
            filteredDocumentGroups &&
            !filteredDocumentGroups.some(dg => dg.documentCode === selectedGroup.documentCode))
            setSelectedGroup(null);
    }, [filteredDocumentGroups, selectedGroup]);
    const allowedFileTypes = useResource(DocumentSettingsResource.detail(), {
        method: "GET"
    })
        .allowedFileTypes.map(x => `.${x}`) // Add "." -prefix to each fileType
        .join(", ");
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, style: {}, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Dropzone, { className: classes.dropZoneArea, disabled: uploadLoading, label: _jsxs(Grid, { item: true, container: true, xs: 12, className: classes.dropZoneInner, direction: "row", wrap: "nowrap", children: [_jsx(Grid, { container: true, item: true, xs: 1, className: classes.circleIconContainer, children: _jsx(AddDocumentUploadIcon, { style: {
                                                width: "40px",
                                                height: "40px"
                                            } }) }), _jsxs(Grid, { item: true, container: true, xs: "auto", className: classes.dropZoneTextContainer, justifyContent: "center", alignItems: "flex-start", direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "body1", className: classes.dropZoneTextUpper, children: _jsx(FormattedMessage, { id: "template-form.section.Document-dropzone-upload.UploadText", defaultMessage: "Velg fil", description: "Logo" }) }) }), _jsx(Grid, { item: true, className: classes.dropZoneTextLower, children: _jsx(Typography, { variant: "caption", className: classes.dropZoneTextLower, children: _jsx(FormattedMessage, { id: "template-form.section.Document-dropzone.ValidFiles", defaultMessage: "Supported files: {validFileTypes}", description: "validFileTypes", values: {
                                                            validFileTypes: allowedFileTypes
                                                        } }) }) })] })] }), files: [], imgBackground: "#FFFFFF", accept: allowedFileTypes, maxSize: 20 ** 19, onChange: files => {
                                setUploadLoading(true);
                                uploadDocument(files[0], selectedGroup && selectedGroup.documentCode, customerContext).then(response => {
                                    response
                                        .json()
                                        .then(result => {
                                        if (result.error) {
                                            setErrorMessages([result.error]);
                                        }
                                        else {
                                            setErrorMessages([]);
                                        }
                                    })
                                        .catch(error => {
                                        setErrorMessages([
                                            intl.formatMessage({
                                                id: "template-form.sections.document-upload.genericuploaderror",
                                                defaultMessage: "Det skjedde en feil."
                                            })
                                        ]);
                                    });
                                    setUploadLoading(false);
                                    if (selectedGroup && selectedGroup.mustSign) {
                                        refetchSigningDocuments({ applicationNumber });
                                        refetchSigners({ applicationNumber, signerType: 0 });
                                    }
                                    refetchDocuments({ applicationNumber, customerContext });
                                });
                            }, onError: messages => setErrorMessages(messages) }) }), _jsx(Grid, { item: true, style: { color: "#f44336" }, children: errorMessages })] }), documentList && documentList.length > 0 ? (_jsx(Grid, { item: true, container: true, xs: 12, style: { background: "transparent" }, children: _jsxs(Suspense, { fallback: _jsx(LinearProgress, {}), children: [_jsx(Grid, { xs: 12, item: true, children: _jsx(Typography, { variant: "body1", className: classes.documentListHeader, children: _jsx(FormattedMessage, { id: "template-form.sections.document-dropzone-upload.uploades-documents-header", description: "Label for Uploaded documents Button", defaultMessage: "Uploaded" }) }) }), _jsx(List, { style: { width: "100%", padding: "unset" }, children: documentList.map(x => (_jsx(ListItem, { style: {
                                    padding: "unset",
                                    width: "100%"
                                }, children: _jsxs(Grid, { container: true, item: true, xs: 12, className: classes.documentListCard, style: {}, children: [_jsx(Grid, { item: true, xs: 1, children: docIcon ? (_jsx(ImageFit, { src: docIcon, alt: "document Icon" })) : (_jsx(DescriptionIcon, {})) }), _jsx(Grid, { item: true, container: true, xs: 10, direction: "column", style: {
                                                alignSelf: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }, children: _jsxs(Link, { href: legacyApiResourceUrl("/DocumentGrid/GetDocument" +
                                                    "?documentId=" +
                                                    x.documentId), style: { width: "inherit" }, className: "file-upload__document-name", underline: "hover", children: [_jsx(Typography, { variant: "body1", noWrap: true, className: classes.documentName, children: x.filename.split(".")[0] }), _jsxs(Typography, { variant: "caption", className: classes.documentFilesize, children: [Math.round(x.fileSize / 1000), " kB"] })] }) }), _jsx(Grid, { item: true, xs: 1, children: _jsx(ClearIcon, { className: classes.clearIcon, onClick: () => {
                                                    setSelectedDocument(x);
                                                    setOpenConfirmDialog(true);
                                                }, style: { cursor: "pointer" } }) })] }) }))) })] }) })) : null, _jsx(ConfirmDialog, { open: openConfirmDialog, text: _jsx(FormattedMessage, { id: "template-form.sections.document-upload.delete-document-confirm", description: "Confirm deletion of uploaded document", defaultMessage: "Er du sikker p\u00E5 at du vil slette dokumentet?" }), onDeny: () => setOpenConfirmDialog(false), onConfirm: () => {
                    setOpenConfirmDialog(false);
                    deleteDocument(selectedDocument.documentId).then(() => {
                        deleteDocumentFromResource({
                            documentId: selectedDocument.documentId
                        });
                        refetchSigningDocuments({ applicationNumber });
                        refetchSigners({ applicationNumber, signerType: 0 });
                        refetchDocuments({ applicationNumber });
                    });
                } })] }));
});
//# sourceMappingURL=DocumentDropzoneSection.js.map